<template>
  <div class="detail-container py-4 px-3 px-md-4">

    <!-- State 1: Loading -->
    <div v-if="!pelanggaran" class="state-card text-center py-5 shadow-sm rounded-4 bg-white">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="text-muted fw-semibold mb-0">Sedang memuat detail pelanggaran...</p>
    </div>

    <!-- State 2: Data Loaded -->
    <div v-else class="detail-content space-y-4">

      <!-- Top Bar Action / Back Button -->
      <div class="d-flex align-items-center justify-content-between mb-3">
        <button class="btn btn-outline-secondary btn-sm rounded-3 d-inline-flex align-items-center gap-1"
          @click="$router.back()">
          <i class="bi bi-arrow-left"></i> Kembali
        </button>
        <span class="badge bg-danger-subtle text-danger px-3 py-2 rounded-pill fw-medium fs-8">
          <i class="bi bi-exclamation-triangle-fill me-1"></i> Terdeteksi ETLE HUB
        </span>
      </div>

      <!-- Hero Header & Jenis Pelanggaran -->
      <div class="header-card mb-4 p-4 rounded-4 text-white shadow-sm">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          
          <!-- Jenis Pelanggaran -->
          <div class="text-start">
            <small class="text-white-50 text-uppercase tracking-wider fw-bold fs-8">Jenis Pelanggaran</small>
            <h3 class="fw-bold mb-0 mt-1 report-type-title">{{ pelanggaran.report_type || 'Pelanggaran Lalu Lintas' }}</h3>
          </div>

          <!-- Nomor Registrasi -->
          <div class="bg-white bg-opacity-10 border border-white border-opacity-25 rounded-3 px-3 py-2 text-md-end">
            <small class="text-white-50 d-block fs-8 fw-semibold">No. Registrasi / Ref</small>
            <span class="font-monospace fw-bold fs-7 tracking-wider text-warning">
              {{ pelanggaran.ref_number || pelanggaran.no_registrasi || '-' }}
            </span>
          </div>

        </div>
      </div>
      
      <!-- Bukti Foto Pelanggaran -->
      <div class="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
        <div class="card-header bg-white border-bottom-0 pt-4 px-4 pb-0 d-flex align-items-center justify-content-between">
          <h5 class="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
            <i class="bi bi-camera-fill text-primary"></i> Bukti Foto Pelanggaran
          </h5>
        </div>
        <div class="card-body p-4 text-center bg-light-subtle">
          <div class="img-wrapper rounded-3 overflow-hidden shadow-sm d-inline-block bg-dark">
            <img :src="fotoUrl || pelanggaran.bukti_foto" alt="Foto Pelanggaran" class="img-fluid violation-img" />
          </div>
        </div>
      </div>

      <div class="row g-4">
        <!-- Section 1: Detail Pelanggaran -->
        <div class="col-12 col-lg-6">
          <div class="card border-0 shadow-sm rounded-4 h-100">
            <div class="card-header bg-white border-bottom pt-3 px-4 pb-3">
              <h5 class="fw-bold text-primary mb-0 d-flex align-items-center gap-2">
                <i class="bi bi-info-circle-fill"></i> Detail Pelanggaran
              </h5>
            </div>
            <div class="card-body p-4">
              <div class="info-list d-flex flex-column gap-3">

                <div class="info-item d-flex align-items-start gap-3 p-2 rounded-3">
                  <div class="icon-box bg-primary-subtle text-primary rounded-3 p-2">
                    <i class="bi bi-calendar-event fs-5"></i>
                  </div>
                  <div>
                    <span class="text-muted fs-8 d-block fw-semibold">Waktu Kejadian</span>
                    <span class="fw-bold text-dark fs-7">{{ formatDate(pelanggaran.inserted_date) }}</span>
                  </div>
                </div>

                <div class="info-item d-flex align-items-start gap-3 p-2 rounded-3">
                  <div class="icon-box bg-danger-subtle text-danger rounded-3 p-2">
                    <i class="bi bi-geo-alt-fill fs-5"></i>
                  </div>
                  <div>
                    <span class="text-muted fs-8 d-block fw-semibold">Lokasi Kejadian</span>
                    <span class="fw-semibold text-dark fs-7">{{ pelanggaran.lokasi || '-' }}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <!-- Section 2: Detail Kendaraan -->
        <div class="col-12 col-lg-6">
          <div class="card border-0 shadow-sm rounded-4 h-100">
            <div class="card-header bg-white border-bottom pt-3 px-4 pb-3">
              <h5 class="fw-bold text-primary mb-0 d-flex align-items-center gap-2">
                <i class="bi bi-car-front-fill"></i> Detail Kendaraan & Pemilik
              </h5>
            </div>
            <div class="card-body p-4">
              <div class="row g-3">

                <div class="col-6">
                  <div class="p-2 bg-light rounded-3 border-start border-primary border-3">
                    <small class="text-muted d-block fs-8"><i class="bi bi-card-text me-1"></i>Plat Nomor</small>
                    <strong class="text-dark fs-7">{{ pelanggaran.plat_number || '-' }}</strong>
                  </div>
                </div>

                <div class="col-6">
                  <div class="p-2 bg-light rounded-3 border-start border-primary border-3">
                    <small class="text-muted d-block fs-8"><i class="bi bi-person me-1"></i>Nama Pemilik</small>
                    <strong class="text-dark fs-7">{{ pelanggaran.pemilik_stnk || '-' }}</strong>
                  </div>
                </div>

                <div class="col-12">
                  <div class="p-2 bg-light rounded-3 border-start border-secondary border-3">
                    <small class="text-muted d-block fs-8"><i class="bi bi-house-door me-1"></i>Alamat Pemilik</small>
                    <span class="text-dark fs-7 fw-medium">{{ pelanggaran.alamat_pemilik || '-' }}</span>
                  </div>
                </div>

                <div class="col-6">
                  <div class="p-2 bg-light rounded-3">
                    <small class="text-muted d-block fs-8">No. Rangka</small>
                    <span class="text-dark fs-8 fw-semibold font-monospace">{{ pelanggaran.noRangka || '-' }}</span>
                  </div>
                </div>

                <div class="col-6">
                  <div class="p-2 bg-light rounded-3">
                    <small class="text-muted d-block fs-8">No. Mesin</small>
                    <span class="text-dark fs-8 fw-semibold font-monospace">{{ pelanggaran.noMesin || '-' }}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { getImgCrossOrigin } from "../Konfirmasi/api";

export default {
  data() {
    return {
      pelanggaran: null,
      fotoUrl: null,
    };
  },
  mounted() {
    this.loadDetailFromSessionOrFetch();
  },
  methods: {
    async loadDetailFromSessionOrFetch() {
      const saved = sessionStorage.getItem("currentViolationDetail");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          this.pelanggaran = parsed;
          await this.loadFoto(parsed.foto);
          return;
        } catch (e) {
          console.warn("Gagal parsing sessionStorage, fetch dari API");
          sessionStorage.removeItem("currentViolationDetail");
        }
      }

      await this.fetchDetailPelanggaran();
    },

    async fetchDetailPelanggaran() {
      const plat_number = this.$route.params.plat_number;
      const noRangka = this.$route.params.noRangka;
      const noMesin = this.$route.params.noMesin;
      const url = `${process.env.VUE_APP_API_URL}/etle/admin-etle/cek-data-kendaraan.php?noPolisi=${plat_number}&noRangka=${noRangka}&noMesin=${noMesin}`;

      try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.status && data.data.length > 0) {
          this.pelanggaran = data.data[0];
          await this.loadFoto(this.pelanggaran.foto);
        } else {
          console.error("Data tidak ditemukan");
        }
      } catch (error) {
        console.error("Gagal mengambil data detail:", error);
      }
    },

    async loadFoto(fotoUrl) {
      try {
        const response = await getImgCrossOrigin(fotoUrl);
        this.fotoUrl = response.config.url;
      } catch (error) {
        console.error("Gagal memuat gambar CORS:", error);
        this.fotoUrl = fotoUrl;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const cleanDateStr = dateString.split('.')[0].replace(' ', 'T');
      const date = new Date(cleanDateStr);

      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleString("id-ID", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.detail-container {
  max-width: 1000px;
  margin: 0 auto;
}

.header-card {
  background: linear-gradient(135deg, #24378b 0%, #172554 100%);
}

.report-type-title {
  font-size: 1.5rem;
  letter-spacing: -0.02em;
}

.img-wrapper {
  max-height: 480px;
  border: 1px solid #e2e8f0;
}

.violation-img {
  max-height: 450px;
  object-fit: contain;
  width: 100%;
}

.icon-box {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Typography Utilities */
.fs-7 {
  font-size: 0.875rem;
}

.fs-8 {
  font-size: 0.75rem;
}

.tracking-wider {
  letter-spacing: 0.05em;
}

.font-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>